import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import API_URL from '../config/api';
import { 
  FiUser, 
  FiBriefcase, 
  FiMapPin, 
  FiCalendar, 
  FiKey,
  FiMail,
  FiArrowLeft,
  FiEdit,
  FiTrash2
} from 'react-icons/fi';

const ViewClient = () => {
  const { clientNumber } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClient();
  }, [clientNumber]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientNumber}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setClient(response.data.client);
    } catch (error) {
      console.error('Error fetching client:', error);
      toast.error('Error al cargar el cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/clients/edit/${clientNumber}`);
  };

  const handleDelete = async () => {
    if (window.confirm(`¿Estás seguro de eliminar al cliente ${client.fullName}?`)) {
      try {
        await axios.delete(`${API_URL}/api/clients/${clientNumber}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Cliente eliminado');
        navigate('/clients');
      } catch (error) {
        toast.error('Error al eliminar cliente');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Cliente no encontrado</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate('/clients')}
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        <FiArrowLeft className="mr-2" /> Volver a clientes
      </button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold">{client.fullName}</h1>
              <p className="text-blue-100 mt-1">#{client.clientNumber}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleEdit}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 flex items-center gap-2"
              >
                <FiEdit /> Editar
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2"
              >
                <FiTrash2 /> Eliminar
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="border-b pb-2">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Información Personal</h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <FiUser className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Nombre completo</p>
                    <p className="font-medium">{client.fullName}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiMail className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{client.email || 'No especificado'}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiBriefcase className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Compañía</p>
                    <p className="font-medium">{client.company || 'No especificada'}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiMapPin className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Dirección</p>
                    <p className="font-medium">{client.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b pb-2">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Fechas</h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <FiCalendar className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Fecha de inicio</p>
                    <p className="font-medium">{formatDate(client.startDate)}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiCalendar className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Fecha de vencimiento</p>
                    <p className="font-medium">{formatDate(client.dueDate)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-b pb-2">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Credenciales</h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <FiKey className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Usuario</p>
                    <p className="font-medium">{client.username || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiKey className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Contraseña</p>
                    <p className="font-medium">{client.clientPassword ? '••••••••' : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b pb-2">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Códigos</h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <FiKey className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Código 2D</p>
                    <p className="font-medium">{client.twoDigitCode}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiKey className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">ZID Code</p>
                    <p className="font-medium">{client.zidCode || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FiKey className="text-gray-400 mr-3 mt-1" size={18} />
                  <div>
                    <p className="text-sm text-gray-600">Account Number</p>
                    <p className="font-medium">{client.cancellationNumber || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Estado</h2>
              <span className={`px-3 py-1 text-sm rounded-full ${
                client.powerStatus === 'activo' ? 'bg-green-100 text-green-800' :
                client.powerStatus === 'espera' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {client.powerStatus === 'activo' ? 'Activo' :
                 client.powerStatus === 'espera' ? 'En Espera' : 'Desconectado'}
              </span>
              {client.urgent && (
                <span className="ml-2 bg-red-600 text-white px-3 py-1 text-sm rounded-full">
                  URGENTE
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewClient;